import { useEffect, useMemo, useRef, useState } from "react"
import { useQueryState } from "nuqs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons"
import { DateTime } from "luxon"
import Dropdown from "../../components/Dropdown"
import DateRangeControl from "../../components/DateRangeControl"
import ApiError from "../../components/ApiError"
import MatchSourceStatCards from "../../components/MatchSourceStatCards"
import {
  useVideoStatsCases,
  useVideoScanFilterOptions,
  useVideoLogs,
  useVideoLogById,
  useVideoLogRow,
} from "../../hooks/useVideoLogs"
import { getDateRange, groupByDate } from "../../utils/datetime"
import { sourceOptions } from "../../constants"
import LogDetailPane from "../EducationalVideoLogs/LogDetailPane"
import LogsSidebar from "../EducationalVideoLogs/LogsSidebar"
import { parseMatchingVideos } from "../EducationalVideoLogs/videoMetrics"

const ALL = "All"
const CUSTOM = "Custom"
const DEFAULT_RANGE = "This Week"
const DATE_RANGE_OPTIONS = [
  "Today",
  "Yesterday",
  "This Week",
  "Last Week",
  "This Month",
  "Last Month",
  "All",
]
const DATE_RANGE_PRESETS = DATE_RANGE_OPTIONS.map((value) => ({
  value,
  label: value,
}))
const PAGE_SIZE = 20

const formatRangeLabel = (startIso, endIso) => {
  if (!startIso || !endIso) return ""
  const start = DateTime.fromISO(startIso)
  const end = DateTime.fromISO(endIso)
  if (!start.isValid || !end.isValid) return ""

  const sameMonth = start.hasSame(end, "month") && start.hasSame(end, "year")
  const startLabel = start.toFormat("MMM d")
  const endLabel = sameMonth ? end.toFormat("d") : end.toFormat("MMM d")
  return `${startLabel} – ${endLabel}`
}

const VideoStats = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [activeSearchTerm, setActiveSearchTerm] = useQueryState("search", {
    defaultValue: "",
  })
  const [dateRange, setDateRange] = useQueryState("dateRange", {
    defaultValue: DEFAULT_RANGE,
  })
  const [dateFrom, setDateFrom] = useQueryState("dateFrom", {
    defaultValue: "",
  })
  const [dateTo, setDateTo] = useQueryState("dateTo", { defaultValue: "" })
  const [issuer, setIssuer] = useQueryState("issuer", { defaultValue: ALL })
  const [source, setSource] = useQueryState("source", { defaultValue: ALL })
  const [selectedLogId, setSelectedLogId] = useQueryState("logId", {
    defaultValue: "",
  })

  const sidebarScrollRef = useRef(null)
  const listRef = useRef(null)
  const detailScrollRef = useRef(null)
  const sentinelRef = useRef(null)

  const { data: filterOptions } = useVideoScanFilterOptions()

  const isCustomDateRange = dateRange === CUSTOM

  const getDateBounds = () => {
    if (isCustomDateRange) {
      return { start: dateFrom || "", end: dateTo || "" }
    }
    if (dateRange === "All") {
      return { start: "", end: "" }
    }
    const range = DATE_RANGE_OPTIONS.includes(dateRange)
      ? dateRange
      : DEFAULT_RANGE
    const interval = getDateRange(range)
    return { start: interval.start.toISODate(), end: interval.end.toISODate() }
  }

  const { start, end } = getDateBounds()
  const timePeriod = start || end ? [start, end].join(",") : undefined
  const rangeLabel = formatRangeLabel(start, end)

  const dateRangeTriggerLabel =
    isCustomDateRange && rangeLabel
      ? rangeLabel
      : isCustomDateRange
        ? CUSTOM
        : dateRange

  const handleSelectDateRangePreset = (range) => setDateRange(range)

  const handleApplyCustomDateRange = (from, to) => {
    setDateFrom(from)
    setDateTo(to)
    setDateRange(CUSTOM)
  }

  const getDateRangeCustomSeed = () => {
    if (isCustomDateRange) return [dateFrom, dateTo]
    if (dateRange === "All") {
      const interval = getDateRange(DEFAULT_RANGE)
      return [interval.start.toISODate(), interval.end.toISODate()]
    }
    const named = DATE_RANGE_OPTIONS.includes(dateRange)
      ? dateRange
      : DEFAULT_RANGE
    const interval = getDateRange(named)
    return [interval.start.toISODate(), interval.end.toISODate()]
  }

  const {
    data: { summary, categories } = {
      summary: { totalRecords: 0 },
      categories: [],
    },
  } = useVideoStatsCases({
    limit: 1,
    filters: {
      timePeriod,
      patientIssuer: issuer === ALL ? undefined : issuer,
      source: source === ALL ? undefined : source,
    },
  })

  const {
    data: { data, total } = { data: [], total: 0 },
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useVideoLogs({
    limit: PAGE_SIZE,
    sortBy: "CreatedDate:DESC",
    filters: activeSearchTerm?.trim()
      ? { searchTerm: activeSearchTerm.trim() }
      : {
          timePeriod,
          patientIssuer: issuer === ALL ? undefined : issuer,
          source: source === ALL ? undefined : source,
        },
  })

  const { data: activeRow, isLoading: isRowLoading } =
    useVideoLogRow(selectedLogId)

  const { data: logDetail, isLoading: isDetailLoading } =
    useVideoLogById(selectedLogId)

  const videos = useMemo(
    () => parseMatchingVideos(activeRow?.MatchingVideos),
    [activeRow]
  )

  const groups = useMemo(
    () => groupByDate(data, (log) => log.CreatedDate),
    [data]
  )

  useEffect(() => {
    if (isLoading || !data.length || selectedLogId) return
    setSelectedLogId(String(data[0].ReportVideosLogId))
  }, [isLoading, data, selectedLogId, setSelectedLogId])

  const filtersKey = `${activeSearchTerm}|${dateRange}|${dateFrom}|${dateTo}|${issuer}|${source}`
  const previousFiltersKey = useRef(filtersKey)

  useEffect(() => {
    if (previousFiltersKey.current === filtersKey) return
    previousFiltersKey.current = filtersKey
    setSelectedLogId("")
  }, [filtersKey, setSelectedLogId])

  useEffect(() => {
    detailScrollRef.current?.scrollTo({ top: 0 })
  }, [selectedLogId])

  useEffect(() => {
    const sentinel = sentinelRef.current
    const root = sidebarScrollRef.current
    if (!sentinel || !root) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { root, rootMargin: "200px", threshold: 0.1 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleSelect = (log) => {
    setSelectedLogId(String(log.ReportVideosLogId))
    listRef.current?.focus()
  }

  const handleClearFilters = () => {
    setDateRange(DEFAULT_RANGE)
    setDateFrom("")
    setDateTo("")
    setIssuer(ALL)
    setSource(ALL)
  }

  const handleClearSearch = () => {
    setSearchTerm("")
    setActiveSearchTerm("")
  }

  const hasActiveFilters =
    dateRange !== DEFAULT_RANGE || issuer !== ALL || source !== ALL

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <div className="flex h-page flex-col gap-3 overflow-hidden pb-4">
      <div className="shrink-0">
        <h1 className="text-2xl font-bold text-gray-900">
          Video Source Insights
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Showing video stats by source
        </p>
      </div>

      <div className="shrink-0 bg-white border border-gray-200 rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label className="text-xs font-medium mb-1 ml-1 text-gray-700">
              DATE RANGE
            </label>
            <DateRangeControl
              customLabel={CUSTOM}
              getCustomSeed={getDateRangeCustomSeed}
              presets={DATE_RANGE_PRESETS}
              triggerLabel={dateRangeTriggerLabel}
              value={dateRange}
              onApplyCustom={handleApplyCustomDateRange}
              onSelectPreset={handleSelectDateRangePreset}
            />
          </div>

          <Dropdown
            label="ISSUER"
            options={[ALL, ...(filterOptions?.patientIssuers || [])]}
            setValue={setIssuer}
            title="Filter by issuer"
            value={issuer}
          />

          {/* "Report" because this page's own "source" is the physician
              match source shown in the stat cards. */}
          <Dropdown
            label="REPORT SOURCE"
            options={[ALL, ...sourceOptions]}
            setValue={setSource}
            title="Filter by report source (Ramsoft / Outside V3)"
            value={source}
          />

          <button
            className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={!hasActiveFilters}
            title="Reset filters to their defaults"
            onClick={handleClearFilters}
          >
            Reset
          </button>

          <div className="flex-1" />

          <div>
            <label className="block text-xs font-medium mb-1 ml-1 text-gray-700">
              ACCESSION / PHYSICIAN
            </label>
            <div className="relative">
              <FontAwesomeIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
                icon={faSearch}
              />
              <input
                className="w-full min-w-[300px] pl-9 pr-9 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search and press Enter"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    setActiveSearchTerm(searchTerm)
                  }
                }}
              />
              {searchTerm && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  title="Clear search"
                  type="button"
                  onClick={handleClearSearch}
                >
                  <FontAwesomeIcon className="w-3 h-3" icon={faTimes} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <MatchSourceStatCards categories={categories} summary={summary} />

      {/* min-h-0 is load-bearing: without it the sidebar's content sizes this
          row and the page grows past the viewport again. */}
      <div className="flex min-h-0 flex-1 gap-5">
        <LogsSidebar
          groups={groups}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          listRef={listRef}
          loadedCount={data.length}
          scrollRef={sidebarScrollRef}
          selectedId={selectedLogId}
          sentinelRef={sentinelRef}
          total={total}
          onLoadMore={fetchNextPage}
          onSelect={handleSelect}
        />

        <LogDetailPane
          detail={logDetail}
          isDetailLoading={isDetailLoading}
          isRowLoading={isRowLoading}
          log={activeRow}
          scrollRef={detailScrollRef}
          selectedId={selectedLogId}
          videos={videos}
        />
      </div>
    </div>
  )
}

export default VideoStats
